import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { startKeyVerification, getClaimedKeys, VerificationResult } from '@/services/keyVerificationService';

const AdminKeyVerification = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [results, setResults] = useState<VerificationResult[]>([]);
    const [claimedKeys, setClaimedKeys] = useState<any[]>([]);
    const [summary, setSummary] = useState({
        total: 0,
        verified: 0,
        failed: 0,
        skipped: 0,
    });

    // Verify access token and auto-start verification
    useEffect(() => {
        const adminToken = import.meta.env.VITE_ADMIN_VERIFY_TOKEN;

        if (token !== adminToken) {
            toast({
                title: 'Accès refusé',
                description: 'Token invalide',
                variant: 'destructive',
            });
            navigate('/');
            return;
        }

        setIsAuthorized(true);
        loadClaimedKeys();

        // Auto-start verification
        setTimeout(() => {
            handleStartVerification();
        }, 500);
    }, [token, navigate, toast]);

    const loadClaimedKeys = async () => {
        try {
            const keys = await getClaimedKeys();
            setClaimedKeys(keys);
        } catch (error) {
            toast({
                title: 'Erreur',
                description: 'Impossible de charger les clés',
                variant: 'destructive',
            });
        }
    };

    const handleStartVerification = async () => {
        setIsVerifying(true);
        setResults([]);
        setSummary({ total: 0, verified: 0, failed: 0, skipped: 0 });

        try {
            const response = await startKeyVerification();

            setResults(response.results);
            setSummary(response.summary);

            toast({
                title: 'Vérification terminée',
                description: `${response.summary.verified} clés activées sur ${response.summary.total} clés vérifiées`,
            });

            // Reload claimed keys to show updated status
            await loadClaimedKeys();
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.message || 'La vérification a échoué',
                variant: 'destructive',
            });
        } finally {
            setIsVerifying(false);
        }
    };

    if (!isAuthorized) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const progressPercentage = summary.total > 0
        ? ((summary.verified + summary.failed + summary.skipped) / summary.total) * 100
        : 0;

    return (
        <AdminLayout>
            <div className="container mx-auto">
                {/* Header */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <Shield className="h-8 w-8 text-primary" />
                            <div>
                                <CardTitle className="text-2xl">Vérification des clés Steam</CardTitle>
                                <CardDescription>
                                    Vérifiez automatiquement si les joueurs ont activé leurs clés Steam
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-4">
                            {isVerifying ? (
                                <div className="flex items-center gap-3 text-primary">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span className="font-medium">Vérification en cours...</span>
                                </div>
                            ) : results.length > 0 ? (
                                <div className="flex items-center gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                    <span className="font-medium text-green-600">
                                        Vérification automatique terminée
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span>Initialisation...</span>
                                </div>
                            )}

                            <div className="text-sm text-muted-foreground ml-auto">
                                {claimedKeys.length} clés claimed au total
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Statistics */}
                {(isVerifying || results.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <CardHeader className="pb-3">
                                <CardDescription>Total</CardDescription>
                                <CardTitle className="text-3xl">{summary.total}</CardTitle>
                            </CardHeader>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardDescription className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                    Vérifiées
                                </CardDescription>
                                <CardTitle className="text-3xl text-green-500">{summary.verified}</CardTitle>
                            </CardHeader>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardDescription className="flex items-center gap-2">
                                    <XCircle className="h-4 w-4 text-red-500" />
                                    Échouées
                                </CardDescription>
                                <CardTitle className="text-3xl text-red-500">{summary.failed}</CardTitle>
                            </CardHeader>
                        </Card>

                        <Card>
                            <CardHeader className="pb-3">
                                <CardDescription className="flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                                    Ignorées
                                </CardDescription>
                                <CardTitle className="text-3xl text-yellow-500">{summary.skipped}</CardTitle>
                            </CardHeader>
                        </Card>
                    </div>
                )}

                {/* Progress Bar */}
                {isVerifying && (
                    <Card>
                        <CardContent className="pt-6">
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Progression</span>
                                    <span>{Math.round(progressPercentage)}%</span>
                                </div>
                                <Progress value={progressPercentage} />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Results Table */}
                {results.length > 0 && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Résultats de la vérification</CardTitle>
                            <CardDescription>Détails de chaque clé vérifiée</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Jeu</TableHead>
                                        <TableHead>Utilisateur</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Raison</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {results.map((result) => (
                                        <TableRow key={result.keyId}>
                                            <TableCell className="font-medium">{result.gameTitle}</TableCell>
                                            <TableCell>{result.userName}</TableCell>
                                            <TableCell>
                                                {result.status === 'verified' && (
                                                    <Badge className="bg-green-500">
                                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                                        Vérifiée
                                                    </Badge>
                                                )}
                                                {result.status === 'failed' && (
                                                    <Badge variant="destructive">
                                                        <XCircle className="h-3 w-3 mr-1" />
                                                        Échec
                                                    </Badge>
                                                )}
                                                {result.status === 'skipped' && (
                                                    <Badge variant="secondary">
                                                        <AlertCircle className="h-3 w-3 mr-1" />
                                                        Ignorée
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {result.reason || '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {/* All Claimed Keys Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Toutes les clés claimed</CardTitle>
                        <CardDescription>Vue d'ensemble de toutes les clés réclamées</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Jeu</TableHead>
                                    <TableHead>Utilisateur</TableHead>
                                    <TableHead>Steam ID</TableHead>
                                    <TableHead>Activée</TableHead>
                                    <TableHead>Date réclamation</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {claimedKeys.map((key) => (
                                    <TableRow key={key.id}>
                                        <TableCell className="font-medium">
                                            {key.games?.title || 'Unknown'}
                                        </TableCell>
                                        <TableCell>{key.profiles?.display_name || 'Unknown'}</TableCell>
                                        <TableCell>
                                            {key.profiles?.steam_id ? (
                                                <Badge variant="outline">{key.profiles.steam_id}</Badge>
                                            ) : (
                                                <span className="text-muted-foreground text-sm">Non renseigné</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {key.is_activated ? (
                                                <Badge className="bg-green-500">Oui</Badge>
                                            ) : (
                                                <Badge variant="secondary">Non</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {key.claimed_at ? new Date(key.claimed_at).toLocaleDateString('fr-FR') : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminKeyVerification;
